/**
 * auth/roles.js — the pure role model, dependency-free.
 *
 * Split out of AuthContext.jsx so content code (Knowledge articles, role
 * chips) can read role labels and the capability matrix without pulling the
 * MSAL-backed auth stack into its import graph. AuthContext re-exports
 * everything here, so existing `from './AuthContext.jsx'` imports keep
 * working.
 */

// ── Role display labels (sidebar / UI) ─────────────────────────────────────────
export const ROLE_LABELS = {
  SuperAdmin:     'Super Admin',
  Supervisor:     'Supervisor',
  SupervisorView: 'Supervisor (view)',
  Admin:          'Admin',
  HoRO:           'Head of RO',
  ClinicManager:  'Clinic Manager',
  ClinicMailbox:  'Clinic Mailbox',
  ReadOnly:       'Read Only',
  Unauthorized:   'Unauthorized',
};

// ── Capability matrix ──────────────────────────────────────────────────────────
// `submitCase` / `editCase` / `changeStatusAndCode` are the three case actions
// SuperAdmins configure in Admin ▸ Roles. Legacy `write` is kept in sync for
// older definitions and is true when either submit or edit is granted.

function caseCaps(submit, edit, statusCode, extra = {}) {
  return {
    submitCase: submit,
    editCase: edit,
    changeStatusAndCode: statusCode,
    write: submit || edit,
    ...extra,
  };
}

export const ROLE_CAPABILITIES = {
  // manageReq = "can add requirements"; respondReq = "can respond to requirements".
  // HoRO + ClinicManager are RESPONDER roles: submit, comment, resolve, respond —
  // but NO editCase (can't edit fields), escalate, add-req, or delete. Mirror of
  // api/src/lib/auth.js. See requirement_permissions_logic.md.
  // dashboardView = analytics Dashboard; knowledgeView = Knowledge guide;
  // caseReportView = case-file Report tab + file/ZIP downloads (Admin tier and
  // up by default — decision 2026-07-21). These replaced the single reportView.
  SuperAdmin:     { ...caseCaps(true, true, true),  comment: true,  resolve: true,  reopen: true,  del: true,  costView: true,  costEdit: true,  dashboardView: true, knowledgeView: true, caseReportView: true,  manageReq: true,  respondReq: true,  assignTags: true,  createTags: true,  privateView: true,  impersonate: true  },
  Supervisor:     { ...caseCaps(true, true, true),  comment: true,  resolve: true,  reopen: true,  del: true,  costView: true,  costEdit: true,  dashboardView: true, knowledgeView: true, caseReportView: true,  manageReq: true,  respondReq: true,  assignTags: true,  createTags: true,  privateView: true,  impersonate: true  },
  SupervisorView: { ...caseCaps(false, false, false), comment: false, resolve: false, reopen: false, del: false, costView: true,  costEdit: false, dashboardView: true, knowledgeView: true, caseReportView: true,  manageReq: false, respondReq: false, assignTags: false, createTags: false, privateView: true,  impersonate: false },
  Admin:          { ...caseCaps(true, true, true),  comment: true,  resolve: true,  reopen: true,  del: true,  costView: false, costEdit: false, dashboardView: true, knowledgeView: true, caseReportView: true,  manageReq: true,  respondReq: true,  assignTags: true,  createTags: false, privateView: true,  impersonate: false },
  HoRO:           { ...caseCaps(true, false, false), comment: true,  resolve: true,  reopen: false, del: false, costView: false, costEdit: false, dashboardView: true, knowledgeView: true, caseReportView: false, manageReq: false, respondReq: true,  assignTags: false, createTags: false, privateView: true,  impersonate: false },
  ClinicManager:  { ...caseCaps(true, false, false), comment: true,  resolve: true,  reopen: false, del: false, costView: false, costEdit: false, dashboardView: true, knowledgeView: true, caseReportView: false, manageReq: false, respondReq: true,  assignTags: false, createTags: false, privateView: false, impersonate: false },
  // ClinicMailbox — a shared clinic login (mirror of api/src/lib/auth.js). No
  // dashboard/reports/private/admin; may submit + comment + respond + self-register.
  ClinicMailbox:  { ...caseCaps(true, false, false), comment: true,  resolve: false, reopen: false, del: false, costView: false, costEdit: false, dashboardView: false, knowledgeView: true, caseReportView: false, manageReq: false, respondReq: true,  assignTags: false, createTags: false, privateView: false, impersonate: false, clinicAccessRequest: true, manageClinicAccess: false },
  ReadOnly:       { ...caseCaps(false, false, false), comment: false, resolve: false, reopen: false, del: false, costView: false, costEdit: false, dashboardView: true, knowledgeView: true, caseReportView: false, manageReq: false, respondReq: false, assignTags: false, createTags: false, privateView: true,  impersonate: false },
  Unauthorized:   { ...caseCaps(false, false, false), comment: false, resolve: false, reopen: false, del: false, costView: false, costEdit: false, dashboardView: false, knowledgeView: false, caseReportView: false, manageReq: false, respondReq: false, assignTags: false, createTags: false, privateView: false, impersonate: false },
};
// Clinic-access approvals inbox — managers who oversee clinics, each SCOPED to
// their own clinics by the API (CM own clinic, HoRO region, Supervisor all).
// Mirror of api/src/lib/auth.js. Country/area custom roles gain it via the
// Roles editor and are scoped the same way.
for (const r of ['SuperAdmin', 'Supervisor', 'HoRO', 'ClinicManager']) ROLE_CAPABILITIES[r].manageClinicAccess = true;
// Case sharing with clinic staff — same manager set (Admin kept as all-scope
// admin tier). Sharing is inherently scoped by case access.
for (const r of ['SuperAdmin', 'Supervisor', 'Admin', 'HoRO', 'ClinicManager']) ROLE_CAPABILITIES[r].shareCaseAccess = true;
// Waiving a required document ("No consent form — <reason>" on an existing
// case, or withdrawing it) is for the people who hold the paperwork: the
// clinic side (ClinicManager + ClinicMailbox) and the Supervisor tier
// (SuperAdmin + Supervisor); Admin / HoRO off. Mirror of api/src/lib/auth.js
// (product-owner decision 2026-09-11). Editable per role in Admin ▸ Roles.
for (const r of Object.keys(ROLE_CAPABILITIES)) ROLE_CAPABILITIES[r].waiveReq = false;
for (const r of ['SuperAdmin', 'Supervisor', 'ClinicManager', 'ClinicMailbox']) ROLE_CAPABILITIES[r].waiveReq = true;
// Managing the Code 4/5 response SLA (lift hold / suppress / resume on a case)
// is likewise Supervisor-tier: SuperAdmin + Supervisor only. Mirror of
// api/src/lib/auth.js (sla_escalation_logic.md §4). Editable in Admin ▸ Roles.
for (const r of Object.keys(ROLE_CAPABILITIES)) ROLE_CAPABILITIES[r].manageSla = false;
for (const r of ['SuperAdmin', 'Supervisor']) ROLE_CAPABILITIES[r].manageSla = true;

export { BUILT_IN_ROLE_PRIORITIES, resolveRolePriority, canAssumeRole } from '@shared/roleSchema.js';
import { normalizeCapabilities, CUSTOM_ROLE_BASE_CAPABILITIES } from '@shared/roleSchema.js';

// ── Custom roles ───────────────────────────────────────────────────────────────
// SuperAdmins define custom roles in Admin ▸ Roles; the definitions live in the
// governed `roleDefinitions` config key (built-in entries carry builtIn: true
// and no capabilities — the matrix above applies). FE capabilities are
// display-only: every endpoint re-derives authz server-side from its own cache.

// True when the role string is one of the built-in matrix rows.
export function isBuiltInRole(role) {
  return !!role && Object.prototype.hasOwnProperty.call(ROLE_CAPABILITIES, role);
}

/** Governed override for a built-in (every built-in except SuperAdmin). */
export function findBuiltInRoleDef(role, roleDefinitions) {
  if (!role || role === 'SuperAdmin' || !isBuiltInRole(role) || !Array.isArray(roleDefinitions)) return null;
  const def = roleDefinitions.find(d => d?.value === role && d?.builtIn === true);
  if (!def || def.enabled === false || !def.capabilities) return null;
  return def;
}

// The enabled custom-role definition for a role string, or null. Fail closed:
// built-in role strings never resolve as custom (so a definition can't shadow
// a built-in), and a missing / disabled / builtIn-flagged entry yields null →
// Unauthorized capabilities.
export function findCustomRoleDef(role, roleDefinitions) {
  if (!role || isBuiltInRole(role) || !Array.isArray(roleDefinitions)) return null;
  const def = roleDefinitions.find(d => d?.value === role && !d.builtIn);
  return def && def.enabled === true ? def : null;
}

// Overlay a custom or governed built-in definition's capability booleans.
// Delegates to the SHARED normalizeCapabilities so FE and API can never drift:
// built-in overrides base on their own matrix row (keys added after the row
// was saved default to code intent — the reportView lesson), custom roles on
// the fail-closed custom base. An explicit stored boolean always wins.
function mergedCapabilities(def) {
  const base = ROLE_CAPABILITIES[def?.value] || CUSTOM_ROLE_BASE_CAPABILITIES;
  return normalizeCapabilities(def?.capabilities || {}, base);
}

// Display label for any role string: built-in label → custom definition label
// (enabled or not — a disabled role should still read nicely) → the raw value.
export function getRoleLabel(role, roleDefinitions) {
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  const def = Array.isArray(roleDefinitions) ? roleDefinitions.find(d => d?.value === role) : null;
  return def?.label || role;
}

// ── Scoped admin access ────────────────────────────────────────────────────────
// Which Admin Panel tabs each role may open. 'all' = the full panel.
// Server-side mirror: api/src/lib/auth.js (canManageTags / canWriteConfigKey +
// SUPERVISOR_CONFIG_KEYS) — keep the two in sync.
export const ADMIN_TABS_BY_ROLE = {
  SuperAdmin: 'all',
  Supervisor: ['Tags & Rules', 'Cost', 'Changelog'],
};

// True when the user may open the given Admin Panel tab.
export function hasAdminTab(user, tab) {
  const tabs = user?.adminTabs;
  return tabs === 'all' || (Array.isArray(tabs) && tabs.includes(tab));
}

// `roleDefinitions` (optional) is the merged governed config list — only
// consulted when the role string is not built-in, so built-in resolution stays
// synchronous and config-free. Unknown/disabled custom role → Unauthorized.
export function attachCapabilities(user, roleDefinitions) {
  if (!user) return user;
  const key = user.isSuperAdmin ? 'SuperAdmin' : user.role;
  const builtInOverride = findBuiltInRoleDef(key, roleDefinitions);
  const customDef = ROLE_CAPABILITIES[key] ? null : findCustomRoleDef(key, roleDefinitions);
  const roleDef = builtInOverride || customDef;
  let caps = ROLE_CAPABILITIES[key];
  if (roleDef?.capabilities) {
    caps = mergedCapabilities(roleDef);
  } else if (!caps && customDef) {
    caps = mergedCapabilities(customDef);
  }
  caps = caps || ROLE_CAPABILITIES.Unauthorized;
  return {
    ...user,
    canSubmitCase:         caps.submitCase,
    canEditCase:           caps.editCase,
    canChangeStatusAndCode: caps.changeStatusAndCode,
    canWriteCases:         caps.submitCase || caps.editCase,
    canComment:           caps.comment,
    canResolveCase:       caps.resolve,
    canReopenCase:        caps.reopen,
    canDeleteCase:        caps.del,
    canSeeCosts:          caps.costView,
    canEditCosts:         caps.costEdit,
    canSeeDashboard:      caps.dashboardView,
    canSeeKnowledge:      caps.knowledgeView,
    // Case-file Report tab + attachment/ZIP downloads.
    canUseCaseReports:    caps.caseReportView,
    canManageAdminConfig: !!user.isSuperAdmin,
    // Two independent capabilities — see requirement_permissions_logic.md.
    canManageRequirements: caps.manageReq,
    canRespondToRequirements: caps.respondReq || caps.editCase,
    // Waive / withdraw a matrix-required document (case-file consent slot
    // Record / Withdraw controls). NOT implied by editCase.
    canWaiveRequirements:  !!caps.waiveReq,
    // Lift hold / Suppress / Resume on the case-file SLA chip. NOT implied by editCase.
    canManageSla:          !!caps.manageSla,
    canAssignTags:         caps.assignTags,
    canCreateTags:         caps.createTags,
    canSeePrivateComments: caps.privateView,
    canImpersonate:        caps.impersonate,
    // Clinic mailbox sub-identity onboarding.
    canRequestClinicAccess: !!caps.clinicAccessRequest,
    canManageClinicAccess:  !!caps.manageClinicAccess,
    canShareCaseAccess:     !!caps.shareCaseAccess,
    // Admin Panel tab scope: 'all' | array of tab names | null (no access) —
    // custom roles never get panel access.
    adminTabs:            ADMIN_TABS_BY_ROLE[key] ?? null,
    // Display name for the sidebar / banner (custom roles use their def label).
    roleLabel:            getRoleLabel(key, roleDefinitions),
    // Per-role PIN session-lock override { inactivityMs?, hiddenGraceMs?, exempt? }.
    // Absent fields inherit the code defaults; PinLockContext reads this.
    sessionLock:          roleDef?.sessionLock || null,
  };
}

// One pseudo-user per assignable role, carrying the same capability booleans a
// real user of that role would have. The Knowledge page evaluates an article's
// visibleTo() predicate against these to *compute* its "Available to" chips,
// so the chips can never drift from the capability matrix above.
// Deliberately built-ins only — custom roles don't get Knowledge chips in v1.
export function usersForAllRoles() {
  return Object.keys(ROLE_CAPABILITIES)
    .filter(role => role !== 'Unauthorized')
    .map(role => attachCapabilities({ role, isSuperAdmin: role === 'SuperAdmin' }));
}
